<template>
  <Teleport to="body"
    ><Transition name="sale-editor"
      ><div
        v-if="visible"
        class="editor-layer"
        @mousedown="backgroundDown"
        @click="backgroundClick"
      >
        <aside
          class="editor"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sale-editor-title"
        >
          <header>
            <div>
              <p>{{ draft._key ? "Modification" : "Nouvelle vente" }}</p>
              <h2 id="sale-editor-title">
                {{ draft._key ? "Modifier la vente" : "Créer une vente" }}
              </h2>
              <span
                >{{ selectedItems.length }} objet{{
                  selectedItems.length > 1 ? "s" : ""
                }}
                sélectionné{{ selectedItems.length > 1 ? "s" : "" }}</span
              >
            </div>
            <button type="button" aria-label="Fermer" @click="requestClose">
              ×
            </button>
          </header>
          <nav>
            <button
              type="button"
              :class="{ active: step === 'details' }"
              @click="step = 'details'"
            >
              Informations</button
            ><button
              type="button"
              :class="{ active: step === 'items' }"
              @click="step = 'items'"
            >
              Objets <b>{{ selectedItems.length }}</b>
            </button>
          </nav>
          <main>
            <section v-if="step === 'details'" class="form-section">
              <div class="form-grid">
                <label class="wide"
                  >Titre de la vente *<input
                    v-model.trim="draft.title"
                    placeholder="Ex. Lot Nintendo 64 en boîte" /></label
                ><label
                  >Canal<select v-model="draft.source">
                    <option
                      v-for="(label, id) in SALE_CHANNELS"
                      :key="id"
                      :value="id"
                    >
                      {{ label }}
                    </option>
                  </select></label
                ><label
                  >Statut<select v-model="draft.status">
                    <option value="draft">Brouillon</option>
                    <option value="listed">En ligne</option>
                    <option value="sold">Vendue</option>
                    <option value="cancelled">Annulée</option>
                  </select></label
                ><label
                  >Prix global<input
                    v-model.number="draft.globalPrice"
                    type="number"
                    min="0"
                    step="0.01" /></label
                ><label
                  >Date<input v-model="draft.saleDate" type="date" /></label
                ><label class="wide"
                  >Lien de l’annonce<input
                    v-model.trim="draft.url"
                    type="url"
                    placeholder="https://..." /></label
                ><label class="wide"
                  >Description<textarea
                    v-model.trim="draft.description"
                    rows="5"
                  ></textarea>
                </label>
              </div>
            </section>
            <section v-else class="items-section">
              <div class="selected-heading">
                <div>
                  <h3>Objets de la vente</h3>
                  <p>
                    Définissez le contenu et, pour une vente terminée, le prix
                    de chaque objet.
                  </p>
                </div>
              </div>
              <div v-if="selectedItems.length" class="selected-list">
                <article v-for="item in selectedItems" :key="item._key">
                  <span>{{ itemName(item).charAt(0).toUpperCase() }}</span>
                  <div>
                    <strong>{{ itemName(item) }}</strong
                    ><small>{{
                      [item.brand, item.model].filter(Boolean).join(" · ") ||
                      typeLabel(item.type)
                    }}</small>
                  </div>
                  <label v-if="draft.status === 'sold'"
                    >Prix<input
                      v-model.number="item.soldPrice"
                      type="number"
                      min="0"
                      step="0.01" /></label
                  ><button
                    type="button"
                    aria-label="Retirer l’objet"
                    @click="removeItem(item._key)"
                  >
                    ×
                  </button>
                </article>
              </div>
              <div v-else class="empty">Aucun objet sélectionné.</div>
              <div class="candidate-box">
                <h3>Ajouter des objets</h3>
                <div class="candidate-filters">
                  <input
                    v-model.trim="candidateSearch"
                    type="search"
                    placeholder="Rechercher dans la collection…"
                    @input="scheduleCandidates"
                  /><select v-model="candidateType" @change="loadCandidates">
                    <option value="">Tous les types</option>
                    <option value="game">Jeux</option>
                    <option value="console">Consoles</option>
                    <option value="device">Périphériques</option>
                  </select>
                </div>
                <div v-if="loadingCandidates" class="candidate-state">
                  Chargement…
                </div>
                <div v-else class="candidates">
                  <button
                    v-for="item in availableCandidates"
                    :key="item._key"
                    type="button"
                    @click="addItem(item)"
                  >
                    <span>＋</span>
                    <div>
                      <strong>{{ itemName(item) }}</strong
                      ><small>{{
                        [item.brand, item.model].filter(Boolean).join(" · ") ||
                        typeLabel(item.type)
                      }}</small>
                    </div>
                  </button>
                  <p v-if="!availableCandidates.length">
                    Aucun autre objet disponible.
                  </p>
                </div>
              </div>
            </section>
          </main>
          <p v-if="errorMessage" class="error" role="alert">
            {{ errorMessage }}
          </p>
          <footer>
            <button
              type="button"
              class="secondary"
              :disabled="saving"
              @click="requestClose"
            >
              Annuler</button
            ><button
              v-if="step === 'details'"
              type="button"
              class="secondary"
              @click="step = 'items'"
            >
              Choisir les objets →</button
            ><button
              type="button"
              class="primary"
              :disabled="saving || !isValid"
              @click="saveSale"
            >
              {{
                saving
                  ? "Enregistrement…"
                  : draft._key
                    ? "Enregistrer la vente"
                    : "Créer la vente"
              }}
            </button>
          </footer>
        </aside>
      </div></Transition
    ></Teleport
  >
</template>
<script>
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import axios from "axios";
import config from "@/config/config.js";
import { SALE_CHANNELS } from "../../sources";
// Today in the user's time ("2026-10-21"), not in UTC (a sale saved at
// 1 a.m. in Paris was dated the day before)
const localToday = () => {
  const today = new Date();
  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
};
const emptySale = () => ({
  _key: "",
  title: "",
  source: "leboncoin",
  status: "draft",
  url: "",
  description: "",
  globalPrice: 0,
  saleDate: "",
});
export default {
  name: "SaleEditorPanel",
  props: {
    visible: { type: Boolean, default: false },
    sale: { type: Object, default: null },
    initialItems: { type: Array, default: () => [] },
  },
  emits: ["close", "saved"],
  setup(props, { emit }) {
    const draft = reactive(emptySale()),
      selectedItems = ref([]),
      candidateItems = ref([]),
      candidateSearch = ref(""),
      candidateType = ref(""),
      loadingCandidates = ref(false),
      saving = ref(false),
      errorMessage = ref(""),
      step = ref("details");
    let timer;
    const itemName = (item) => item.name || item.title || "Objet sans nom",
      typeLabel = (type) =>
        ({ game: "Jeu", console: "Console", device: "Périphérique" })[type] ||
        "Objet",
      availableCandidates = computed(() => {
        const keys = new Set(selectedItems.value.map((item) => item._key));
        return candidateItems.value.filter((item) => !keys.has(item._key));
      }),
      isValid = computed(() =>
        Boolean(draft.title.trim() && selectedItems.value.length),
      );
    const hydrate = () => {
      const base = props.sale || emptySale();
      // The API stores an empty link or text as null
      Object.assign(draft, emptySale(), base, {
        title: base.title || "",
        url: base.url || "",
        description: base.description || "",
        saleDate: base.saleDate || "",
        globalPrice: Number(base.globalPrice) || 0,
      });
      selectedItems.value = (
        Array.isArray(base.items) && base.items.length
          ? base.items
          : props.initialItems
      ).map((item) => ({ ...item, soldPrice: item.soldPrice ?? null }));
      candidateSearch.value = "";
      candidateType.value = "";
      errorMessage.value = "";
      step.value =
        props.initialItems.length && !base._key ? "items" : "details";
    };
    const loadCandidates = async () => {
      if (!props.visible) return;
      loadingCandidates.value = true;
      try {
        const response = await axios.get(
          `${config.apiUrl}/sales/items-candidates`,
          {
            params: {
              sale_id: draft._key || undefined,
              contains: candidateSearch.value || undefined,
              type: candidateType.value || undefined,
              page: 0,
              limit: 100,
            },
          },
        );
        candidateItems.value = Array.isArray(response.data?.result)
          ? response.data.result
          : [];
      } catch (error) {
        console.error("Chargement des objets disponibles impossible", error);
      } finally {
        loadingCandidates.value = false;
      }
    };
    const scheduleCandidates = () => {
        clearTimeout(timer);
        timer = setTimeout(loadCandidates, 250);
      },
      addItem = (item) =>
        selectedItems.value.push({
          ...item,
          soldPrice: item.soldPrice ?? null,
        }),
      removeItem = (key) => {
        selectedItems.value = selectedItems.value.filter(
          (item) => item._key !== key,
        );
      };
    const saveSale = async () => {
      if (!isValid.value) return;
      saving.value = true;
      errorMessage.value = "";
      try {
        const saleDate =
            draft.status === "sold" && !draft.saleDate
              ? localToday()
              : draft.saleDate,
          payload = {
            title: draft.title.trim(),
            source: draft.source,
            status: draft.status,
            url: (draft.url || "").trim(),
            description: (draft.description || "").trim(),
            globalPrice: Number(draft.globalPrice) || 0,
            saleDate,
            items: selectedItems.value.map((item) => ({
              item_id: item._key,
              soldPrice:
                draft.status === "sold" ? Number(item.soldPrice) || 0 : null,
            })),
          },
          url = draft._key
            ? `${config.apiUrl}/sales/${draft._key}`
            : `${config.apiUrl}/sales`,
          response = await axios.post(url, payload, {
            headers: { "Content-Type": "application/json" },
          });
        emit("saved", response.data?.result || null);
      } catch (error) {
        console.error("Enregistrement de la vente impossible", error);
        errorMessage.value =
          "Impossible d’enregistrer la vente pour le moment.";
      } finally {
        saving.value = false;
      }
    };
    // What the form held when opened: closing after a change asks first
    let opened = "";
    const snapshot = () =>
      JSON.stringify([
        draft,
        selectedItems.value.map((item) => [item._key, item.soldPrice]),
      ]);
    const requestClose = () => {
      if (
        !saving.value &&
        snapshot() !== opened &&
        !window.confirm("Fermer sans enregistrer la vente ?")
      )
        return;
      emit("close");
    };
    // A click on the background closes, not the end of a text selection
    // started in the form and released outside
    let pressedOnBackground = false;
    const backgroundDown = (event) => {
      pressedOnBackground = event.target === event.currentTarget;
    };
    const backgroundClick = (event) => {
      if (pressedOnBackground && event.target === event.currentTarget)
        requestClose();
      pressedOnBackground = false;
    };
    watch(
      () => props.visible,
      (visible) => {
        if (visible) {
          hydrate();
          opened = snapshot();
          loadCandidates();
        }
      },
      { immediate: true },
    );
    onBeforeUnmount(() => clearTimeout(timer));
    return {
      SALE_CHANNELS,
      addItem,
      availableCandidates,
      candidateSearch,
      candidateType,
      draft,
      errorMessage,
      isValid,
      itemName,
      loadCandidates,
      loadingCandidates,
      removeItem,
      backgroundClick,
      backgroundDown,
      requestClose,
      saveSale,
      saving,
      scheduleCandidates,
      selectedItems,
      step,
      typeLabel,
    };
  },
};
</script>
<style scoped>
.editor-layer {
  position: fixed;
  inset: 0;
  z-index: 1250;
  background: rgba(15, 23, 36, 0.48);
  backdrop-filter: blur(2px);
}
.editor {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: min(720px, 100%);
  height: 100%;
  flex-direction: column;
  background: #fff;
  box-shadow: -18px 0 55px rgba(15, 23, 36, 0.2);
  color: #202b3c;
  font-family: "DM Sans", sans-serif;
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
}
.editor > header {
  display: flex;
  justify-content: space-between;
  padding: 24px 28px 20px;
  border-bottom: 1px solid #e3e8ef;
}
.editor header p {
  margin: 0 0 7px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.editor header h2 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: 21px;
}
.editor header span {
  display: block;
  margin-top: 6px;
  color: #84909f;
  font-size: 11px;
}
.editor header button {
  width: 35px;
  height: 35px;
  border: 1px solid #d8dfe7;
  border-radius: 8px;
  background: #fff;
  color: #657184;
  font-size: 20px;
}
.editor > nav {
  display: flex;
  gap: 24px;
  padding: 0 28px;
  border-bottom: 1px solid #e3e8ef;
}
.editor nav button {
  position: relative;
  height: 52px;
  border: 0;
  background: none;
  color: #7a8595;
  font-size: 12px;
  font-weight: 700;
}
.editor nav button b {
  margin-left: 5px;
  padding: 2px 6px;
  border-radius: 10px;
  background: #eaf7f2;
  color: #168a64;
}
.editor nav button.active {
  color: #202b3c;
}
.editor nav button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: #168a64;
  content: "";
}
.editor > main {
  min-height: 0;
  flex: 1;
  padding: 24px 28px;
  overflow: auto;
  background: #fafbfc;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.form-grid label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #4d596a;
  font-size: 11px;
  font-weight: 700;
}
.form-grid .wide {
  grid-column: 1/-1;
}
.form-grid input,
.form-grid select,
.form-grid textarea,
.candidate-filters input,
.candidate-filters select,
.selected-list label input {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #d2dae4;
  border-radius: 7px;
  background: #fff;
  color: #283446;
  font: inherit;
  font-weight: 400;
}
.form-grid textarea {
  resize: vertical;
}
.selected-heading h3,
.candidate-box h3 {
  margin: 0;
  font-size: 14px;
}
.selected-heading p {
  margin: 5px 0 14px;
  color: #7b8797;
  font-size: 11px;
}
.selected-list {
  overflow: hidden;
  border: 1px solid #dfe5ec;
  border-radius: 9px;
  background: #fff;
}
.selected-list article {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px;
  border-bottom: 1px solid #edf0f4;
}
.selected-list article > span {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 7px;
  background: #eaf7f2;
  color: #168a64;
  font-weight: 800;
}
.selected-list article > div {
  min-width: 0;
  flex: 1;
}
.selected-list strong,
.selected-list small {
  display: block;
}
.selected-list strong {
  font-size: 12px;
}
.selected-list small {
  margin-top: 3px;
  color: #84909f;
  font-size: var(--meta-size);
}
.selected-list label {
  width: 90px;
  color: #6f7c8d;
  font-size: var(--meta-size);
}
.selected-list label input {
  min-height: 32px;
}
.selected-list article > button {
  width: 29px;
  height: 29px;
  border: 1px solid #d8dfe7;
  border-radius: 6px;
  background: #fff;
}
.candidate-box {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #e1e6ed;
}
.candidate-filters {
  display: grid;
  grid-template-columns: 1fr 150px;
  gap: 9px;
  margin: 10px 0;
}
.candidates {
  max-height: 270px;
  overflow: auto;
  border: 1px solid #dfe5ec;
  border-radius: 9px;
  background: #fff;
}
.candidates > button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  background: #fff;
  text-align: left;
}
.candidates > button:hover {
  background: #fafcfd;
}
.candidates > button > span {
  color: #168a64;
  font-size: 16px;
}
.candidates > button > div {
  min-width: 0;
}
.candidates strong,
.candidates small {
  display: block;
}
.candidates strong {
  font-size: 11px;
}
.candidates small {
  margin-top: 3px;
  color: #84909f;
  font-size: var(--meta-size);
}
.candidates p,
.candidate-state,
.empty {
  padding: 25px;
  color: #7b8797;
  font-size: 11px;
  text-align: center;
}
.editor > .error {
  margin: 0;
  padding: 10px 28px;
  border-top: 1px solid #f0d8d5;
  background: #fff5f4;
  color: #a4453f;
  font-size: 11px;
}
.editor > footer {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding: 16px 28px;
  border-top: 1px solid #e3e8ef;
}
.editor footer button {
  height: 40px;
  padding: 0 15px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
}
.secondary {
  border: 1px solid #d5dce5;
  background: #fff;
  color: #566276;
}
.primary {
  border: 0;
  background: #172235;
  color: #fff;
}
.primary:disabled {
  opacity: 0.45;
}
.sale-editor-enter-active,
.sale-editor-leave-active {
  transition: opacity 0.18s;
}
.sale-editor-enter-from,
.sale-editor-leave-to {
  opacity: 0;
}
@media (max-width: 600px) {
  .editor > header,
  .editor > main,
  .editor > footer {
    padding-inline: 18px;
  }
  .editor > nav {
    padding-inline: 18px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-grid .wide {
    grid-column: auto;
  }
  .candidate-filters {
    grid-template-columns: 1fr;
  }
  .editor > footer button {
    flex: 1;
  }
}
</style>
